// Quote Builder — door colour select, tier-grouped. Shared.
//
// Lifted out of door-picker.ts (colourOptGroup ~1033, stockTypeBadge ~119,
// the select + badge assembly ~1044-1072) so the SJB frontal section groups
// and labels colours the same way instead of listing them flat with no
// pricing context. door-picker.ts renders through here too.
//
// The one behavioural difference between the two callers is deliberate and is
// expressed as data, not a flag: the door picker paints ONE tier at a time —
// its flow picks a tier first and cross-tier bleed-in is a regression it
// guards against — while the SJB picker has no tier step and shows every
// non-empty tier at once. Both call renderColourSelect with the groups they
// want; the module does not decide.

import { escapeHtml } from "./formatters";

/** Door pricing/lead-time tiers. Defined here rather than in door-picker.ts
 *  now that two pickers key off it. */
export type DoorType = "stock" | "signature" | "pto" | "paintplus" | "bespoke" | "primed";

/** Lead-time / pricing context shown under the dropdown. */
export function stockTypeBadge(stockType: DoorType | "stock"): { label: string; modifier: string } {
  switch (stockType) {
    case "pto":       return { label: "Paint to order · longer lead time", modifier: "pto" };
    // Paint Plus is the one tier where price and lead time disagree: it is
    // priced as a stocked colour but painted to order. Saying "stock pricing"
    // alone would promise a stock lead time; saying "paint to order" alone
    // would imply the PTO price. Both halves are stated for that reason.
    case "paintplus": return { label: "Paint Plus · stock price, longer lead time", modifier: "paintplus" };
    case "signature": return { label: "Signature · stock lead time",       modifier: "signature" };
    case "bespoke":   return { label: "Bespoke · made to order",           modifier: "bespoke" };
    case "primed":    return { label: "Primed · ready to paint on site",   modifier: "primed" };
    case "stock":
    default:          return { label: "Stock colour · stock pricing",      modifier: "stock" };
  }
}

/** Group heading — the pricing implication, not just the tier name, so the
 *  retailer sees the consequence before selecting rather than after. */
export function tierLabel(tier: DoorType): string {
  switch (tier) {
    case "signature": return "Signature · stock lead time";
    case "pto":       return "Paint to order · longer lead time";
    case "paintplus": return "Paint Plus · stock price, longer lead time";
    case "bespoke":   return "Bespoke · made to order";
    case "primed":    return "Primed · ready to paint on site";
    case "stock":
    default:          return "Stocked colours · fast delivery";
  }
}

export interface ColourGroup {
  tier: DoorType;
  colours: readonly string[];
  /** Overrides tierLabel(tier) when a caller wants its own wording. */
  label?: string;
}

/** `<optgroup>` for one tier. Values are "tier|colour" so a single change
 *  handler recovers both — the convention door-picker's setColourUnified
 *  already expects. */
export function colourOptGroup(
  group: ColourGroup,
  selectedTier: DoorType | "",
  selectedColour: string,
): string {
  if (!group.colours.length) return "";
  const opts = group.colours.map((c) => {
    const sel = selectedTier === group.tier && selectedColour === c;
    return `<option value="${group.tier}|${escapeHtml(c)}"${sel ? " selected" : ""}>${escapeHtml(c)}</option>`;
  }).join("");
  const label = group.label ?? tierLabel(group.tier);
  return `<optgroup label="${escapeHtml(label)} (${group.colours.length})">${opts}</optgroup>`;
}

export interface ColourSelectOpts {
  groups: ColourGroup[];
  selectedTier: DoorType | "";
  selectedColour: string;
  /** data-role on the <select>. */
  role: string;
  /** Shown when every group is empty. */
  emptyText?: string;
  className?: string;
}

export function renderColourSelect(o: ColourSelectOpts): string {
  const filled = o.groups.filter((g) => g.colours.length > 0);
  if (!filled.length) {
    return `<div class="qb-door-empty">${escapeHtml(o.emptyText ?? "No colours uploaded for this range yet")}</div>`;
  }
  const opts = `<option value="">— Select a colour —</option>`
    + filled.map((g) => colourOptGroup(g, o.selectedTier, o.selectedColour)).join("");
  return `<select class="${escapeHtml(o.className ?? "qb-door-input")}" data-role="${escapeHtml(o.role)}">${opts}</select>`;
}

/** The "✓ Fir Green · Stock colour · stock pricing" line under the select. */
export function renderTierBadge(colour: string, tier: DoorType | "stock"): string {
  if (!colour && tier !== "bespoke") return "";
  const badge = stockTypeBadge(tier);
  return `<div class="qb-door-tier qb-door-tier--${badge.modifier}">✓ ${
    escapeHtml(colour || "Bespoke")} · ${escapeHtml(badge.label)}</div>`;
}
